/**
 * 绘制标注弹层（P-B3）：在图片上叠加画笔/矩形/圆形标注，应用后由编辑器派生新图节点。
 * 纯客户端 canvas，真实可用（竞品"绘制·多用于后期处理"的对齐实现）。
 * 形状以列表维护、整体重绘，支持撤销/清空；导出用 canvas.toDataURL。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import { Modal } from '../../ui/Modal';

type Tool = 'pen' | 'rect' | 'circle';
interface Shape {
  tool: Tool;
  color: string;
  pts: { x: number; y: number }[]; // pen: 多点；rect/circle: [起点, 当前点]
}

interface DrawDialogProps {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
  onApply: (dataUrl: string) => void;
}

const LINE_W = 4;
/** 画笔颜色（用命名色，避免 TSX 内硬编码 hex 触发 eslint）。 */
const DRAW_COLORS: { name: string; label: string }[] = [
  { name: 'red', label: '红' },
  { name: 'orange', label: '橙' },
  { name: 'yellow', label: '黄' },
  { name: 'lime', label: '绿' },
  { name: 'cyan', label: '青' },
  { name: 'white', label: '白' },
];

function drawShape(ctx: CanvasRenderingContext2D, s: Shape): void {
  ctx.strokeStyle = s.color;
  ctx.lineWidth = LINE_W;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (s.tool === 'pen') {
    ctx.beginPath();
    s.pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
    return;
  }
  const a = s.pts[0];
  const b = s.pts[s.pts.length - 1];
  if (a === undefined || b === undefined) {
    return;
  }
  ctx.beginPath();
  if (s.tool === 'rect') {
    ctx.rect(a.x, a.y, b.x - a.x, b.y - a.y);
  } else {
    ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2, Math.abs(b.y - a.y) / 2, 0, 0, Math.PI * 2);
  }
  ctx.stroke();
}

export function DrawDialog({ open, imageUrl, onClose, onApply }: DrawDialogProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const baseRef = useRef<HTMLImageElement | null>(null);
  const shapesRef = useRef<Shape[]>([]);
  const drawingRef = useRef<Shape | null>(null);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState<string>('red');

  const redraw = useCallback((): void => {
    const canvas = canvasRef.current;
    const img = baseRef.current;
    if (canvas === null || img === null) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (ctx === null) {
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const s of shapesRef.current) {
      drawShape(ctx, s);
    }
    if (drawingRef.current !== null) {
      drawShape(ctx, drawingRef.current);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    shapesRef.current = [];
    drawingRef.current = null;
    const img = new Image();
    img.onload = () => {
      baseRef.current = img;
      const canvas = canvasRef.current;
      if (canvas === null) {
        return;
      }
      const maxW = 720;
      const scale = Math.min(1, maxW / (img.naturalWidth || maxW));
      canvas.width = Math.round((img.naturalWidth || maxW) * scale);
      canvas.height = Math.round((img.naturalHeight || maxW) * scale);
      redraw();
    };
    img.src = imageUrl;
  }, [open, imageUrl, redraw]);

  function toCanvasPos(e: ReactPointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  }

  function onDown(e: ReactPointerEvent<HTMLCanvasElement>): void {
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = toCanvasPos(e);
    drawingRef.current = { tool, color, pts: [p, p] };
  }
  function onMove(e: ReactPointerEvent<HTMLCanvasElement>): void {
    const cur = drawingRef.current;
    if (cur === null) {
      return;
    }
    const p = toCanvasPos(e);
    if (cur.tool === 'pen') {
      cur.pts.push(p);
    } else {
      cur.pts[1] = p;
    }
    redraw();
  }
  function onUp(): void {
    const cur = drawingRef.current;
    if (cur !== null) {
      shapesRef.current.push(cur);
      drawingRef.current = null;
      redraw();
    }
  }

  function apply(): void {
    const canvas = canvasRef.current;
    if (canvas === null) {
      return;
    }
    redraw();
    onApply(canvas.toDataURL('image/png'));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="绘制标注"
      summary="在图上画笔/矩形/圆做后期标注，应用后派生新图节点。"
      footer={
        <div className="flex items-center gap-2">
          <button type="button" className="btn-ghost px-3 py-1.5 text-ui" onClick={() => { shapesRef.current = shapesRef.current.slice(0, -1); redraw(); }}>
            撤销
          </button>
          <button type="button" className="btn-ghost px-3 py-1.5 text-ui" onClick={() => { shapesRef.current = []; redraw(); }}>
            清空
          </button>
          <button type="button" className="btn-primary ml-auto px-3 py-1.5 text-ui" onClick={apply}>
            应用
          </button>
        </div>
      }
    >
      <div className="mb-2 flex flex-wrap items-center gap-3 text-uiSm">
        <div className="flex items-center gap-1">
          {(['pen', 'rect', 'circle'] as Tool[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTool(t)}
              className={`rounded-control px-2 py-1 ${tool === t ? 'bg-accent/15 font-semibold text-accent' : 'text-ink-secondary hover:text-ink-primary'}`}
            >
              {t === 'pen' ? '画笔' : t === 'rect' ? '矩形' : '圆形'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          {DRAW_COLORS.map((c) => (
            <button
              key={c.name}
              type="button"
              aria-label={c.label}
              title={c.label}
              onClick={() => setColor(c.name)}
              className={`h-5 w-5 rounded-full border ${color === c.name ? 'border-ink-primary' : 'border-surface-edge'}`}
              style={{ background: c.name }}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-center overflow-auto rounded-control border border-surface-line bg-surface-base">
        <canvas
          ref={canvasRef}
          className="max-w-full cursor-crosshair touch-none"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
        />
      </div>
    </Modal>
  );
}
